import * as React from "react";
import {cx}       from "emotion";

import "./emulation-modal.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";
import {Responsive}     from "@Common/ui-components/responsive";
import {equalDeep}      from "@Common/utils/objects";

import {showOfficeEmulation} from "../logic/show-office-emulation";
import {ModalLayout}         from "../modal-layout/modal-layout";
import {SelectUserPanel}     from "../select-user-panel/select-user-panel";

import {SessionListingsPlugin} from "./session-listings-plugin";

export const EmulationModal = ({
    plugins = undefined,
    forceUserLevel = undefined,
    limitByBenefit = '',
    next: rootNext
}:Types.Xpresso.Common.EmulationModal.EmulationModalProps) => cs(
    ["modal", ({}, next) => ModalService3<() => void>({
        xdQaName: "Emulation Modal",
        width: 552,
        next: ({params: onSelect, close}) => next({
            onSelect: typeof onSelect === "function" ? onSelect : null,
            close,
        }),
        rootNext,
    })],

    consumeContext("session"),
    consumeContext("userLevels"),
    ["plugins", (_, next) => cs(
        ["sessionListingsPlugin", (_, next) => SessionListingsPlugin({next})],
        ({sessionListingsPlugin}) => next(
            combinePlugins([
                sessionListingsPlugin,
                ...plugins??[],
            ].filter(v=>v))
        ),
    )],
    ["multiBranch", ({userLevels}, next) => next(showOfficeEmulation(userLevels))],

    ["forcedBranchId", ({multiBranch, session}, next) => next(multiBranch ? (
        undefined
    ) : (
        session.value.branchId
    ))],

    ["emulating", (_, next) => UseState({next})],

    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            mobile: 500,
        },
        next,
    }}/>],
    ["panel", ({multiBranch, forcedBranchId}, next) => SelectUserPanel({multiBranch, forcedBranchId, limitByBenefit, next})],
    ({modal, panel, emulating, session, multiBranch, forcedBranchId, plugins, responsive}) => ModalLayout({
        title: multiBranch ? (
            "Office & User Selection"
        ) : (
            "User Selection"
        ),
        content: (
            <div className={cx("emulation-modal-6ed", {multiBranch})}>
                {responsive.ge("mobile") && (
                    <div className="message">
                        {multiBranch ? (
                            "Please select the office and user you want to emulate"
                        ) : (
                            "Please select the user you want to emulate"
                        )}
                    </div>
                )}
                <div className="form">
                    {panel.render()}
                </div>
                {responsive.lt("mobile") && (
                    <div className="message">
                        {multiBranch ? (
                            "Please select the office and user you want to emulate"
                        ) : (
                            "Please select the user you want to emulate"
                        )}
                    </div>
                )}

                {!equalDeep(
                    {
                        ...panel.value,
                        ...forcedBranchId && {branchId: forcedBranchId},
                    },
                    session.value
                ) && plugins.warnings.map((warning, i) => (
                    <div className="warning" key={i}>
                        <b>Warning!</b> {warning}
                    </div>
                ))}
            </div>
        ),
        buttons: [
            {
                label: "Clear Selection",
                disabled: (forcedBranchId || !panel.value?.branchId) && (!panel.value?.agentId && !panel.value?.isBranchOrder),
                onClick: () => panel.clearSelection(),
            },

            (() => {

                const newEmulation = {
                    ...panel.value,
                    ...forcedBranchId && {branchId: forcedBranchId},
                };

                return {
                    label: emulating.value ? (
                        emulating.value.agentId ? (
                            "Emulating Agent..."
                        ) : emulating.value.isBranchOrder ? (
                            "Emulating Office Order..."
                        ) : emulating.value.branchId ? (
                            "Emulating Office..."
                        ) : (
                            "Clearing emulation..."
                        )
                    ) : (
                        "Confirm Selection"
                    ),
                    primary: true,
                    disabled: (
                        (equalDeep(newEmulation, session.value) && !modal.onSelect) ||
                        emulating.value ||
                        forceUserLevel && (
                            !(panel.value.agentId || panel.value?.isBranchOrder) ||
                            !multiBranch && !forcedBranchId
                        )
                    ),
                    onClick: async () => {
                        plugins.beforeEmulate();


                        const appliedEmu = await (async () => {
                            if (equalDeep(newEmulation, session.value)) {
                                return newEmulation;
                            } else {
                                emulating.onChange(newEmulation);
                                try {
                                    return await session.applyEmulation(newEmulation);
                                } finally {
                                    emulating.onChange(null);
                                }
                            }
                        })();

                        modal.onSelect?.(appliedEmu);
                        modal.close();
                    },
                };
            })(),
        ],
    }),
);

const combinePlugins = (plugins) => ({
    warnings: plugins.map((p) => p.warning),
    beforeEmulate: () => {
        plugins.forEach((p) => p.beforeEmulate?.());
    },
});



